/********************************************************************
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this 
 * file in accordance with the terms of the Adobe license agreement 
 * accompanying it. 
 *******************************************************************/

import { FunctionComponent, VNode, cloneElement } from 'preact';
import { HTMLAttributes } from 'preact/compat';
import { classes } from '@adobe-commerce/elsie/lib';
import '@adobe-commerce/elsie/components/ActionButtonGroup/ActionButtonGroup.css';
import { useCallback, useState } from 'preact/hooks';
import { Children } from 'react';
import { ActionButtonProps } from '@adobe-commerce/elsie/components/ActionButton';

export interface ActionButtonGroupOption {
  value: string | null;
  text: string;
  icon?: VNode<SVGElement>;
  disabled?: boolean;
}

export interface ActionButtonGroupProps extends HTMLAttributes<HTMLDivElement> {
  variant?: 'primary' | 'secondary';
  activeOption?: string;
  disabled?: boolean;
  dividers?: boolean;
  children: VNode<ActionButtonProps>[] | VNode<ActionButtonProps>;
  handleSelect?(value: string): void;
}

export const ActionButtonGroup: FunctionComponent<ActionButtonGroupProps> = ({
  className,
  variant = 'primary',
  activeOption,
  disabled = false,
  dividers = true,
  children,
  handleSelect,
  ...props
}) => {
  const [activeOptionState, setActiveOptionState] = useState(activeOption);

  const handleClick = useCallback(
    (option: any) => {
      const isOptionDisabled = disabled || option.props.disabled;
      if (!isOptionDisabled) {
        setActiveOptionState(option.props.value);
        if (handleSelect) {
          handleSelect(option.props.value);
        }
      }
    },
    [handleSelect, setActiveOptionState, disabled]
  );

  const buttonGroupOptions = Children.map(
    children,
    (child: VNode<ActionButtonProps>) => {
      const isOptionDisabled = disabled || child.props.disabled;
      const isOptionActive = child.props.value === activeOptionState;
      return cloneElement(child, {
        disabled: isOptionDisabled,
        active: isOptionActive,
        onClick: () => handleClick(child),
        className: classes([
          'dropin-action-button-group__option',
          `dropin-action-button-group__option--${variant}`,
          ['dropin-action-button-group__option--active', isOptionActive],
          ['dropin-action-button-group__option--with-dividers', dividers],
        ]),
      });
    }
  );

  return (
    <div
      role="group"
      {...props}
      className={classes([
        'dropin-action-button-group',
        `dropin-action-button-group--${variant}`,
        className,
      ])}
    >
      {buttonGroupOptions}
    </div>
  );
};
